import React, { useState, useEffect, useCallback, useRef } from "react";
import { addAnswerKey, deleteAnswerKey } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function AdministrativeAnswerKeyAdminPanel({ darkMode }) {
  const { threeTable, fetchThreeTable, fetchThreeTableSection, threeTableHasMore } = useGlobalData();
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({ jobSlug: "" });
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (!threeTable?.jobs?.length) {
      fetchThreeTable();
    }
  }, [threeTable?.jobs?.length, fetchThreeTable]);

  useEffect(() => {
    setJobs(threeTable.jobs || []);
  }, [threeTable.jobs]);

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      setIsLoading(true);
      try {
        await fetchThreeTableSection("answerKeys", false, { limit: PAGE_LIMIT, offset: 0 });
      } finally {
        if (!ignore) setIsLoading(false);
      }
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll fetch
  const fetchMoreAnswerKeys = useCallback(async () => {
    setIsFetchingMore(true);
    try {
      const offset = Array.isArray(threeTable.answerKeys) ? threeTable.answerKeys.length : 0;
      await fetchThreeTableSection("answerKeys", false, { limit: PAGE_LIMIT, offset });
    } finally {
      setIsFetchingMore(false);
    }
  }, [fetchThreeTableSection, threeTable.answerKeys]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!threeTableHasMore.answerKeys || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreAnswerKeys();
    }
  }, [threeTableHasMore.answerKeys, isFetchingMore, fetchMoreAnswerKeys]);

  // Attach scroll event
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addAnswerKey(formData);
    alert("Answer Key added!");
    setFormData({ jobSlug: "" });
    // Refresh global state (replace with first page)
    await fetchThreeTableSection("answerKeys", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this answer key?")) {
      await deleteAnswerKey(id);
      alert("Answer Key deleted!");
      // Refresh global state (replace with first page)
      await fetchThreeTableSection("answerKeys", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;
  const filteredAnswerKeys = Array.isArray(threeTable.answerKeys)
    ? threeTable.answerKeys.filter((answerKey) => !formData.jobSlug || answerKey.jobSlug === formData.jobSlug)
    : [];

  if (role !== "administrative") {
    return <div className="admin-panel-error">❌ Access denied! Only Administrative users can access this panel.</div>;
  }

  return (
    <div className="resource-admin-panel resource-admin-panel--blue">
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">Administrative Answer Key Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Answer key entries ko cleaner workflow ke saath manage karo. Job choose karo, item add karo aur saved list me direct actions use karo.</p>
        </div>
        <div className="resource-admin-panel__pill">Answer Key</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Answer Key</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Answer Key Entries</h3>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ul>
            {filteredAnswerKeys.map((answerKey) => {
              const isPending = answerKey.status === "pending";
              const statusColor = isPending ? "#f7b500" : "#2ecc40";
              return (
                <li key={answerKey._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span>{getJobTitle(answerKey.jobSlug)}</span>
                  <span style={{
                    background: statusColor,
                    color: "#fff",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontWeight: 600,
                    fontSize: 13
                  }}>
                    {answerKey.status === "pending" ? "Pending" : "Approved"}
                  </span>
                  {isPending && (
                    <button onClick={() => handleDelete(answerKey._id)} style={{ marginLeft: 8 }}>Delete</button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!threeTableHasMore.answerKeys && !isLoading && filteredAnswerKeys.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more answer keys.</div>}
      </div>
    </div>
  );
}

export default AdministrativeAnswerKeyAdminPanel;
